import React, { useState } from 'react';
import { Link } from '@mui/material';
import { useSelector } from 'react-redux';
import { useGlobalization } from '@Translations/useGlobalization';
import { NotificationBanner } from '@Components/NotificationBanner/NotificationBanner.component';
import { useIsRiskTemplatesEnabled } from '@Hooks/useIsRiskTemplatesEnabled';
import { selectTemplateLabel } from '@Selectors/RiskTemplateApply.selectors';
import { selectRiskSetting } from '@Selectors/Risk.selectors';
import { selectRiskTemplateCatalogTemplates } from '@Selectors/RiskTemplateCatalog.selectors';
import {
	dismissTemplateBanner,
	isTemplateBannerDismissed,
} from '@Utils/templateBanner.storage';
import { TemplateInfoDrawer } from './TemplateInfoDrawer.component';

interface Props {
	configId: string;
}

// "You started from {template}" banner, pinned to the very top of the risk
// setup shell (above the breadcrumb). Shows only for a model that was created
// from a template — and only for THAT model (matched on config id). Dismissing
// persists per config in localStorage so it never comes back. "Learn more"
// opens a drawer explaining the template. Rendered with the shared design-system
// notification banner (info variant).
//
// The apply-session templateLabel (from the ephemeral templateApply reducer)
// only exists while the "New risk from template" flow is running, so it is
// gone as soon as the user navigates away and back. To keep the banner
// visible after that, fall back to the templateId persisted on the risk
// config itself, resolving a friendly label from the template catalog when
// it has been loaded (and falling back to the raw id otherwise).
export const TemplateStartBanner = ({ configId }: Props) => {
	const { t } = useGlobalization();
	const enabled = useIsRiskTemplatesEnabled();
	const templateLabel = useSelector(selectTemplateLabel);
	const riskSetting = selectRiskSetting();
	const catalogTemplates = useSelector(selectRiskTemplateCatalogTemplates);
	const [dismissed, setDismissed] = useState(false);
	const [drawerOpen, setDrawerOpen] = useState(false);

	const templateId = riskSetting.templateId;
	const catalogLabel = templateId
		? catalogTemplates.find(template => template.id === templateId)?.label
		: undefined;
	const label = templateLabel ?? catalogLabel ?? templateId;

	if (
		!enabled ||
		dismissed ||
		isTemplateBannerDismissed(configId) ||
		!label
	) {
		return null;
	}

	const dismiss = () => {
		setDismissed(true);
		dismissTemplateBanner(configId);
	};

	return (
		<>
			<NotificationBanner
				variant="info"
				onClose={dismiss}
				dataCy="template-banner">
				<b>
					{t('Starting from the {{label}} template', {
						label,
					})}
				</b>{' '}
				{t(
					'Components, weights and scores are pre-filled below. Review the data-coverage badges and the Summary, adjust anything, then Run.',
				)}{' '}
				<Link
					component="button"
					underline="hover"
					sx={{
						fontSize: 13,
						fontWeight: 700,
						verticalAlign: 'baseline',
					}}
					onClick={() => setDrawerOpen(true)}>
					{t('Learn more about the template')}
				</Link>
			</NotificationBanner>
			<TemplateInfoDrawer
				open={drawerOpen}
				onClose={() => setDrawerOpen(false)}
			/>
		</>
	);
};

export default TemplateStartBanner;
